import type { Player } from '../game/identity';
import { allTools, partnerForTool } from '../game/partners';
import { toolProgress } from '../scenarios';
import { play } from '../game/sound';
import { DetectiveMark } from './DetectiveMark';
import { RTNav } from './RTNav';
import { SiteFooter } from './SiteFooter';

interface Props {
  onStart: () => void;
  /** Who is signed in, if anyone. Null is a normal, playable state. */
  player: Player | null;
  onSignIn: () => void;
  onSignOut: () => void;
  onLeaderboard: () => void;
  /** Present only for a session the server confirmed may edit content. */
  onAdmin?: (() => void) | undefined;
}

/**
 * The title screen.
 *
 * Public: no sign-in, no email, nothing asked for. Someone arriving from a link
 * should be able to see what this is and which tools it covers before deciding
 * whether to hand over an address — the previous flow put a verification form
 * in front of a stranger who had no idea what the site was.
 *
 * The roster is generated from the tool registry, so a tool added there appears
 * here with no change to this file, and cannot be advertised without levels
 * behind it.
 *
 * Only tools that have levels are listed. The roster used to include the rest
 * marked "In preparation", which advertised four products the game could not
 * actually let anyone play — and named vendors whose clearance is still open.
 * A tool reappears here by having a level, not by being announced.
 */
export function Splash({ onStart, player, onSignIn, onSignOut, onLeaderboard, onAdmin }: Props) {
  const live = allTools().filter((tool) => toolProgress(tool.id).available);

  return (
    <div className="panel panel--splash">
      <RTNav />
      <div className="panel__inner">
        <div className="splash__mark">
          <DetectiveMark size={72} />
        </div>

        <p className="case-eyebrow">A ReleaseTEAM game</p>
        <h1 className="splash__title">DevTool Detective</h1>

        <p className="lede splash__lede">
          Somebody has misconfigured the tools your delivery pipeline runs on.
          The evidence is on screen and the arrows are pointing at it. Decide
          which ones are real.
        </p>

        <button
          type="button"
          className="btn btn--wide btn--large"
          onClick={() => {
            play('select');
            onStart();
          }}
        >
          Start investigating
        </button>

        {/*
          Repeated from the picker on purpose. This is the first screen, and
          somebody who never clicks through would otherwise have no way to sign
          in, no way to sign out, and no sight of the admin menu at all.
        */}
        <p className="identity-bar identity-bar--splash">
          {player ? (
            <>
              <span>
                Signed in as <strong>{player.name}</strong>
              </span>
              <button type="button" onClick={onSignOut}>
                Sign out
              </button>
            </>
          ) : (
            <>
              <span>Not signed in</span>
              <button type="button" onClick={onSignIn}>
                Sign in
              </button>
            </>
          )}
        </p>

        <button
          type="button"
          className="btn btn--ghost btn--wide"
          onClick={() => {
            play('coin');
            onLeaderboard();
          }}
        >
          Top scores
        </button>

        {onAdmin && (
          <button type="button" className="btn btn--ghost btn--wide admin-entry" onClick={onAdmin}>
            Level admin
          </button>
        )}

        {/* No tools with levels means no line-up to show. Rendering the
            heading over an empty list would read as a broken page rather than
            an early one. */}
        {live.length > 0 && (
          <>
            <h2>The tools</h2>
            <ul className="roster">
              {live.map((tool) => {
                const partner = partnerForTool(tool.id);
                const { ready, target } = toolProgress(tool.id);
                return (
                  <li key={tool.id} className="roster__item">
                    <span className="roster__id">
                      <span className="roster__name" style={{ color: partner.accent }}>
                        {tool.short}
                      </span>
                      {/* GitLab's product is called GitLab; printing the maker
                          under it twice just looks like a mistake. */}
                      {partner.name !== tool.short && (
                        <span className="roster__by">{partner.name}</span>
                      )}
                    </span>
                    <span className="roster__state roster__state--live">
                      {ready} of {target} cases
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <p className="fineprint">
          Every case names the product it depicts and who makes it. This game is
          made by ReleaseTEAM and is not affiliated with, endorsed by or
          sponsored by any of the vendors shown.{' '}
          <a href="#attribution">Attribution</a>
        </p>

        <SiteFooter />
      </div>
    </div>
  );
}
